import { useEffect, useMemo, useState } from "react";
import { Box, RefreshCw, ScanSearch, ShieldCheck } from "lucide-react";
import type { Asset, PlantState } from "./data";
import ExtruderVisual from "./ExtruderVisual";
import { loadRemotePlantState } from "./remoteData";
import "./visualWorkspace.css";

export default function VisualWorkspace() {
  const [db, setDb] = useState<PlantState | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [lineId, setLineId] = useState("");

  async function reload() {
    setLoading(true);
    setMessage("");
    try {
      const state = await loadRemotePlantState();
      if (!state) throw new Error("Secure plant data is unavailable.");
      setDb(state);
      setLineId((current) => current || state.lines.find((line) => line.name === "Line 1")?.id || state.lines[0]?.id || "");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to load visual configuration.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  const line = db?.lines.find((item) => item.id === lineId);
  const lineAssets = useMemo(() => db?.assets.filter((asset) => asset.lineId === lineId && asset.status !== "Retired") ?? [], [db, lineId]);
  const extruders = useMemo(() => lineAssets.filter((asset) => !asset.parentId && /extruder/i.test(`${asset.name} ${asset.type}`)), [lineAssets]);
  const [selectedExtruderId, setSelectedExtruderId] = useState("");
  const extruder = extruders.find((asset) => asset.id === selectedExtruderId) ?? extruders[0];

  useEffect(() => {
    if (extruders.length && !extruders.some((item) => item.id === selectedExtruderId)) setSelectedExtruderId(extruders[0].id);
  }, [extruders, selectedExtruderId]);

  const visualAsset: Asset | undefined = extruder ?? (line ? {
    id: `visual-placeholder-${line.id}`,
    tag: `${line.name.replace(/\s+/g, "-").toUpperCase()}-EXTRUDER-UNMAPPED`,
    lineId: line.id,
    order: 0,
    name: `${line.name} Extruder`,
    type: "Extruder",
    manufacturer: "Unknown",
    model: "Unknown",
    serial: "Unknown",
    status: "In Service",
    criticality: "A",
    verificationState: "Incomplete",
    completeness: 0,
    specs: [],
    capabilities: [],
    bom: [],
  } : undefined);

  return <div className="vw-shell">
    <header className="vw-header">
      <div><span className="vw-eyebrow">VISUAL EQUIPMENT INTELLIGENCE</span><h1>Machine Visual View</h1><p>Current installed configuration linked to real asset records, parts, approved alternates, and suppliers.</p></div>
      <button className="vw-refresh" disabled={loading} onClick={() => void reload()}><RefreshCw size={16}/> Refresh</button>
    </header>

    <div className="vw-security"><ShieldCheck size={16}/><div><strong>Live plant data</strong><span>No generic component is treated as installed unless a current asset record maps to it.</span></div></div>

    <div className="vw-toolbar">
      <label>Production line<select value={lineId} onChange={(event) => { setLineId(event.target.value); setSelectedExtruderId(""); }}>{db?.lines.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
      {extruders.length > 1 && <label>Extruder<select value={extruder?.id || ""} onChange={(event) => setSelectedExtruderId(event.target.value)}>{extruders.map((item) => <option value={item.id} key={item.id}>{item.tag} · {item.name}</option>)}</select></label>}
      <div className="vw-line-status"><span>{line?.name || "Line"}</span><strong>{line?.completeness ?? 0}% documented</strong></div>
    </div>

    {message && <div className="vw-message">{message}</div>}
    {loading && <div className="vw-empty"><ScanSearch size={36}/><h2>Loading current machine configuration</h2><p>Reading the secured asset hierarchy and BOM.</p></div>}
    {!loading && db && !extruder && visualAsset && <div className="vw-notice"><Box size={18}/><div><strong>No active extruder asset is documented on {line?.name || "this line"} yet.</strong><span>The visual remains available as an unmapped shell. Gray zones are not assumed to be installed; they will link automatically as real records are added.</span></div></div>}
    {!loading && db && visualAsset && <ExtruderVisual db={db} asset={visualAsset} onOpenAsset={() => {}} />}
  </div>;
}
